import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, InputAdornment, Radio, RadioGroup, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt } from 'react-router';

import { DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import HTTPError from '../components/HTTPError';
import InfoTip from '../components/InfoTip';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import QuillEditor from '../components/QuillEditor';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import { a11yProps } from '../components/TabPanel';
import UploadDocumentButton from '../components/UploadDocumentButton';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix } from '../utils';
import validURL from '../utils/validators/url';
import { ConfirmationDialogText, FRIENDLY_NAME_HELP, FRIENDLY_NAME_MAX_LENGTH } from './utils';
import { getPreviousLinkFileOrTextState } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: '-0.25rem',
        [theme.breakpoints.down('sm')]: {
            marginTop: "-3rem"
        },
    },
    gridRoot: {
        overflowY: 'auto',
        height: `calc(${theme.cp.contentHeight} - 408px)`,
        paddingTop: '0.5rem',
        padding: 0,
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 14rem)`,
            zIndex: 10,
            marginRight: theme.spacing(0),
            marginLeft: theme.spacing(0),
        },
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
}));

const validateInput = (item) => {
    const errors = {};
    let canSave = true;
    if (!item?.name || item?.name?.length <= 0) {
        if (item?._changed) {
            errors.name = { error: true, helperText: 'Name is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const getComparableState = (item) => {
    const a = {
        ...item,
    };
    delete a._changed;
    return JSON.stringify(a);
};

const PreTripItemEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'pre_trip_item';
    const { hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const [syncActive, setSyncActive] = React.useState(false);
    const [, setServerErrors] = React.useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [item, setItem] = React.useState(props.data);
    const [currentTab, setCurrentTab] = React.useState(0);
    const [originalState, setOriginalState] = React.useState(getComparableState(props.data));

    const currentState = getComparableState(item);
    const isChanged = originalState !== currentState;

    const [linkFileOrText, setLinkFileOrText] = React.useState(getPreviousLinkFileOrTextState(item));
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);

    const [FileErrorState, setFileErrorState] = React.useState(false);
    const [LinkUrlErrorState, setLinkUrlErrorState] = React.useState(false);
    const [TextErrorState, setTextErrorState] = React.useState(false);
    const { canSave, errors } = validateInput(item);
    const hasWritePerm = hasPerm(resource, 'write')

    const onLicenseeChange = (id) => {
        setItem((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const handleChange = (ev) => {
        setItem((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };
    const handleCheckboxChange = (ev) => {
        setItem((prevState) => ({ ...prevState, [ev.target.name]: ev.target.checked, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };
    const onCategoryChange = (ev, nv) => {
        setItem((prevState) => ({ ...prevState, item_type: nv, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };
    const onAccessTagsChange = (ev, atags, reason) => {
        setItem((prevState) => ({ ...prevState, atag: atags, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };
    const onFileSelect = (file, prevfile) => {
        setItem((prevState) => ({ ...prevState, attachment: file, upload: !!file, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
        setFileErrorState(false);
    };

    const handleSave = (ev) => {
        // Remove file/text/links based on radio selection
        if (linkFileOrText === 'link') {
            item.attachment = null;
            item.text = null;
        }
        if (linkFileOrText === 'text') {
            item.attachment = null;
            item.url = null;
        }
        if (linkFileOrText === 'file') {
            item.url = null;
            item.text = null;
        }

        setSyncActive(true);
        if (item.url) {
            item.url = checkAndAddHttpPrefix(item.url);
        }
        (async () => {
            try {
                const res = await dataAPI(UPDATE, resource, { id: item.id, data: item, upload: item.upload });
                if (res.statusCode === 200) {
                    enqueueSnackbar('Trip item updated', { variant: 'success' });
                    props.onChange(res.data);
                    setOriginalState(getComparableState(item));
                    setItem((prevState) => ({ ...prevState, _changed: false }));
                    props.setDetailsDirty && props.setDetailsDirty(false); 
                } else if (res.statusCode === 406) {
                    enqueueSnackbar('Trip item validation error', { variant: 'warning' });
                    setServerErrors(res.data.errors);
                }
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const doDelete = (ev) => {
        props.onDelete && props.onDelete(props.data);
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_pretrip_item':
            case 'delete_pretrip_items':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleLinkURLOnBlur = () => {
        if (validURL(item.url)) {
            setLinkUrlErrorState(false);
        } else {
            setLinkUrlErrorState(true);
        }
    };

    // const handleChangeRadio = (e) => {
    //     setLinkFileOrText(e.target.value)
    //     props.setDetailsDirty && props.setDetailsDirty(true);

    // }

    let requiredUrl = linkFileOrText === 'link' && !item.url ? true : false;
    const originalDisplayState = getPreviousLinkFileOrTextState(item);


    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    };

    React.useEffect(() => {
        setFileErrorState(false);
        setTextErrorState(false);
        if (linkFileOrText === 'file') {
            if (!item.attachment) {
                setFileErrorState(true);
            }
        }

        if (linkFileOrText === 'text') {
            if (!item.text || item.text === '') {
                setTextErrorState(true);
            }
        }
    }, [item, linkFileOrText]);


    const handleChangeRadio = (ev) => {
        setLinkFileOrText(null);
        setFileErrorState(false);
        setTextErrorState(false);
        if (ev.target.name === 'link') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }
        if (ev.target.name === 'text') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }
        if (ev.target.name === 'file') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }

        if (ev.target.name !== originalDisplayState) {
            setItem((prevState) => ({ ...prevState, display_changed: true }));
        } else {
            setItem((prevState) => {
                let item_place_holder = { ...prevState };
                delete item_place_holder.display_changed;
                return { ...item_place_holder };
            });
        }
        props.setDetailsDirty && props.setDetailsDirty(true);
    }
    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }

    if (!item.id) {
        return <HTTPError resource={resource} id={props.data?.id || props.itemId} code={404} />
    }
    return (
        <FormLicenseeContext.Provider value={toLicenseeId(item.tour_operator)}>
        <div className={classes.root}>
            <PageMeta title={`Edit Trip Item - ${item.name}`} />
            <Prompt when={isChanged && hasPerm(resource, 'write')} message="Are you sure you want to leave without saving changes?" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" >
                <Grid item xs={12} container alignContent="center" mr={-2} style={{ backgroundColor: '#F5F5F5', height: "64px" }}>
                    <Grid item xs={8} mt={0.25} pl={2} >
                        <Typography variant="h6">Trip Item Details</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end">
                        <Tooltip title="Close">
                            <Button aria-label="close-preview" onClick={props.onClose}>
                                <CloseIcon style={{ color: 'black' }} />
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid item xs={12} container direction="row"  >
                    <Grid item xs={12} style={{ backgroundColor: 'white', zIndex: 10000 }}>
                        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                            <Tab label="General" {...a11yProps(0)} />
                            {hasPerm(resource, 'view_history') && <Tab label="Change history" {...a11yProps(1)} />}
                            {item.id && <Tab label="Used by" {...a11yProps(2)} />}
                        </Tabs>
                    </Grid>
                    {/* <TabPanel {...tabPanelProps(0)} > */}
                    {currentTab === 0 && <>
                        <Grid item xs={12} p={1} className={classes.gridRoot}>
                            <Grid item xs={12} mb={'12px'}>
                                <LicenseeFormField value={item.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                            </Grid>
                            <Grid item xs={12} mb={'12px'}>
                                <TextField
                                    disabled={!hasWritePerm}
                                    helperText={`${64 - item.name.length} characters left`}
                                    {...errors.name}
                                    required
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    name="name"
                                    label="Name"
                                    value={item.name || ''}
                                    onChange={handleChange}
                                    inputProps={{ maxLength: 64 }}
                                />
                            </Grid>
                            <Grid item xs={12} mb={'12px'}>
                                {/* CP-6187: what guests see in the app; the backend stores a blank as NULL. */}
                                <TextField
                                    disabled={!hasWritePerm}
                                    helperText={`${FRIENDLY_NAME_MAX_LENGTH - (item.friendly_name || '').length} characters left`}
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    name="friendly_name"
                                    label="Friendly Name (optional)"
                                    value={item.friendly_name || ''}
                                    onChange={handleChange}
                                    inputProps={{ maxLength: FRIENDLY_NAME_MAX_LENGTH }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoTip placement="top-end" title={FRIENDLY_NAME_HELP} sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} mb={'12px'}>
                                <TextField
                                    disabled={!hasWritePerm}
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    name="note"
                                    label="Note"
                                    value={item.note || ''}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} mb={'12px'}>
                                <RemoteAutocomplete disabled={!hasWritePerm} resource="pre_trip_item_type" urlSuffix={null} label="Category" variant="filled" size="small" onChange={onCategoryChange} value={item.item_type} />
                            </Grid>
                            {/* <Grid item xs={12} container direction="row" spacing={2}>
                                <Grid item xs={12}>
                                    <Button color="primary" variant="outlined" size="small" onClick={toggleLinkIsFile}>
                                        <InsertLinkIcon className={classes.icon} /> Add Link
                                    </Button>
                                </Grid>
                                <Grid item xs={12} >
                                    <UploadDocumentButton file={item.attachment} onSelect={onFileSelect} size="small" />
                                </Grid>
                            </Grid> */}
                            <Grid item xs={12} mb={'12px'}>
                                <AccessTagsDropdown disabled={!hasWritePerm} value={item.atag} onChange={onAccessTagsChange} />
                            </Grid>
                            <Grid item xs={12} mb={'12px'}>
                                <FormControl style={{ width: '100%', marginTop: '1rem' }} disabled={!hasWritePerm}>
                                    <Typography style={{ fontWeight: 'bold' }} >Display when tapped</Typography>
                                    <RadioGroup
                                        aria-labelledby="display-when-tapped"
                                        onChange={(e) => handleChangeRadio(e)}
                                        value={linkFileOrText}
                                        sx={{ display: 'flex', flexDirection: 'row' }}
                                    >
                                        <FormControlLabel value="link" control={<Radio color="primary" />} label="Link" name="link" />
                                        <FormControlLabel value="file" control={<Radio color="primary" />} label="File" name="file" />
                                        {item.item_type?.rendering_type !== 2 && <FormControlLabel value="text" name="text" control={<Radio color="primary" />} label="Text" />}

                                        {linkFileOrText === 'text' && item.item_type?.name !== 'List without checkboxes' && (
                                            <QuillEditor {...errors.text} fullWidth name="text" label="Text" value={item.text || ''} onChange={(content) => handleChange({target: {name: 'text', value: content}})} />
                                        )}

                                        {linkFileOrText === "file" &&
                                            <Grid item xs={12}>
                                                <Box pt={1} pb={1}> <UploadDocumentButton disabled={!hasWritePerm} file={item.attachment} onSelect={onFileSelect} size="small" indirect_link /></Box>
                                            </Grid>
                                        }
                                        {linkFileOrText === "link" &&
                                            <Grid item xs={12}>
                                                <TextField
                                                    disabled={!hasWritePerm}
                                                    {...errors.url}
                                                    variant="filled"
                                                    size="small"
                                                    fullWidth
                                                    name="url"
                                                    label="Link"
                                                    placeholder="http://example.com"
                                                    value={item.url || ''}
                                                    onChange={handleChange}
                                                    onBlur={handleLinkURLOnBlur}
                                                    error={LinkUrlErrorState}
                                                    helperText={LinkUrlErrorState && 'Please enter a valid URL.'}
                                                />

                                                <FormGroup disabled={!hasWritePerm}>
                                                    <FormControlLabel control={<Checkbox name="deep_link" color="primary" onChange={handleCheckboxChange} checked={item.deep_link} />} label="Deep Link" />
                                                    <FormHelperText>Open in Safari vs. In-App Browser</FormHelperText>
                                                </FormGroup>
                                            </Grid>
                                        }

                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </>}
                    {hasPerm(resource, 'view_history') && currentTab === 1 && (
                        <Grid item xs={12} p={1} className={classes.gridRoot} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <RevisionHistoryDialog
                                open={true}
                                resource={resource}
                                id={item.id}
                                layout="narrow"
                                style={{ flex: 1, minHeight: 0 }}
                            />
                        </Grid>
                    )}
                    {item.id && currentTab === 2 && (
                        <Grid item xs={12} p={1} className={classes.gridRoot} style={{ overflow: 'auto' }}>
                            <UsedByPanel resource={resource} id={item.id} layout="narrow" />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={handleSave}
                    submitDisabled={!hasPerm(resource, 'write') || !canSave || LinkUrlErrorState || requiredUrl || FileErrorState || TextErrorState || !isChanged}
                    onCancel={props.onClose}
                    cancelDisabled={syncActive}
                />
            </Box>
        </div >
        </FormLicenseeContext.Provider>
    );
};

export default PreTripItemEdit;
